import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import type { ReactElement, MouseEvent } from 'react'
import type { FieldError } from 'react-hook-form'
import type { IBuilderConfig, IBuilderConfigYAxis } from '~/types/graph-builder'

import BuilderFormSection from '../BuilderFormSection'

import DropdownMenu from '~/components/DropdownMenu'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { GRAPH_TYPE } from '~/const/graphs-builder'

import './styles.scss'

const unitOptions = [
  { label: 'Duration', value: 'duration_in_seconds' },
  { label: 'Euro', value: 'euro' },
  { label: 'Percentage', value: 'percent' },
  { label: 'Raw', value: 'raw' },
]

interface Props {
  axisIndex: number
  onDelete: (axisIndex: number) => void
  onSelectFieldChange: (field: keyof IBuilderConfig, selectedOption: IOption | IOption[]) => void
  primaryColor?: string
  secondaryColor?: string
  title: string
}

const YAxisFields = ({
  axisIndex,
  onDelete,
  onSelectFieldChange,
  primaryColor,
  secondaryColor,
  title,
}: Props): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors },
    register,
    watch,
  } = useFormContext()

  const castedYAxesErrors = errors.yAxes as unknown as Record<keyof IBuilderConfigYAxis, FieldError>[]

  const watchSeries0GraphType = watch('series.0.graphType')

  const menu = useMemo(
    () => [
      {
        items: [
          {
            label: 'Delete Y-axis',
            onClick: () => {
              onDelete(axisIndex)
            },
          },
        ],
      },
    ],
    [onDelete, axisIndex]
  )

  return (
    <BuilderFormSection primaryColor={primaryColor} secondaryColor={secondaryColor} title={title}>
      {axisIndex > 0 ? (
        <DropdownMenu
          className="builder-form-section__dropdown-menu"
          menu={menu}
          onMouseDown={(e: MouseEvent) => e.stopPropagation()}
        />
      ) : null}
      <div className="y-axis-fields-component">
        {watchSeries0GraphType !== GRAPH_TYPE.RAW ? (
          <TextInput
            disabled
            error={castedYAxesErrors?.[axisIndex]?.name?.message}
            label="Name"
            placeholder="Y-axis name"
            {...register(`yAxes.${axisIndex}.name`)}
          />
        ) : null}
        <Controller
          control={control}
          name={`yAxes.${axisIndex}.unit`}
          render={({ field }) => (
            <SelectInput
              {...field}
              error={castedYAxesErrors?.[axisIndex]?.unit?.message}
              label="Unit"
              onChange={(value: unknown) =>
                onSelectFieldChange(`yAxes.${axisIndex}.unit` as keyof IBuilderConfig, value as IOption)
              }
              options={unitOptions}
              placeholder="Select a unit"
              required
              value={unitOptions.find(({ value }) => value === field.value)}
            />
          )}
        />
        <TextInput
          error={castedYAxesErrors?.[axisIndex]?.precision?.message}
          label="Precision"
          required
          {...register(`yAxes.${axisIndex}.precision`, { valueAsNumber: true })}
          type="number"
        />
      </div>
    </BuilderFormSection>
  )
}

export default YAxisFields
